import { Link } from "react-router-dom";
import { Shield, ShieldAlert, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMfaStatus } from "@/hooks/useMfaStatus";
import { MFA_ENABLED } from "@/config/mfaFlags";
import { cn } from "@/lib/utils";

/**
 * Header CTA. States:
 *  - Enrolled                  → green, "2FA Enabled"
 *  - Not enrolled, no deadline → amber, "Enable 2FA"
 *  - Not enrolled, >3 days     → amber, "Enable 2FA · Required in X days"
 *  - Not enrolled, ≤3 days     → red,   "Enable 2FA · Required soon"
 *  - Not enrolled, past due    → red,   "Enable 2FA · Required now"
 */
export default function MfaSecurityCta({ className }: { className?: string }) {
  const { loading, enrolled, requiredAfter } = useMfaStatus();

  if (!MFA_ENABLED) return null;

  if (loading) {
    return (
      <Link to="/account/security" aria-label="Account security">
        <Button variant="ghost" size="sm" className={cn("gap-2", className)}>
          <Shield className="h-4 w-4" />
          <span className="hidden sm:inline">Security</span>
        </Button>
      </Link>
    );
  }

  if (enrolled) {
    return (
      <Link to="/account/security" aria-label="Two-factor authentication enabled">
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "gap-2 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100",
            "dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-950/60",
            className,
          )}
        >
          <ShieldCheck className="h-4 w-4" />
          <span className="hidden sm:inline">2FA Enabled</span>
        </Button>
      </Link>
    );
  }

  // Not enrolled — compute countdown state from authoritative deadline.
  const msLeft = requiredAfter ? requiredAfter.getTime() - Date.now() : null;
  const days = msLeft !== null ? Math.ceil(msLeft / (24 * 3600 * 1000)) : null;

  let countdown: string | null = null;
  let urgent = false;
  if (msLeft !== null) {
    if (msLeft <= 0) {
      countdown = "Required now";
      urgent = true;
    } else if (days !== null && days <= 3) {
      countdown = "Required soon";
      urgent = true;
    } else if (days !== null) {
      countdown = `Required in ${days} day${days === 1 ? "" : "s"}`;
    }
  }

  return (
    <Link
      to="/account/security"
      aria-label={countdown ? `Enable 2FA — ${countdown}` : "Enable two-factor authentication"}
    >
      <Button
        size="sm"
        className={cn(
          "gap-2 relative shadow-sm ring-1",
          urgent
            ? "bg-red-600 text-white hover:bg-red-500 ring-red-700/40"
            : "bg-amber-500 text-amber-950 hover:bg-amber-400 ring-amber-600/40",
          className,
        )}
      >
        <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5">
          <span
            className={cn(
              "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75",
              urgent ? "bg-red-400" : "bg-amber-400",
            )}
          />
          <span
            className={cn(
              "relative inline-flex rounded-full h-2.5 w-2.5",
              urgent ? "bg-red-500" : "bg-amber-500",
            )}
          />
        </span>
        <ShieldAlert className="h-4 w-4" />
        <span className="hidden sm:inline">Enable 2FA</span>
        <span className="sm:hidden">2FA</span>
        {countdown && (
          <span
            className={cn(
              "hidden md:inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              urgent
                ? "bg-red-900/30 text-white"
                : "bg-amber-950/15 text-amber-950",
            )}
          >
            {countdown}
          </span>
        )}
      </Button>
    </Link>
  );
}
